import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { mapApiBookingsToBookingRows } from "design-components";
import { bookingsListInitialState, type PrepareBookingsListForCustomerScopePayload } from "./bookings.models";
import { fetchBookings } from "./bookings.thunk";

const bookingsListSlice = createSlice({
  name: "bookingsList",
  initialState: bookingsListInitialState,
  reducers: {
    setBookingsSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setBookingsSort: (
      state,
      action: PayloadAction<{ sortingOrder: "asc" | "desc"; sortingField?: string }>,
    ) => {
      state.sortingOrder = action.payload.sortingOrder;
      if (action.payload.sortingField) {
        state.sortingField = action.payload.sortingField;
      }
    },
    setBookingsCustomerFilters: (state, action: PayloadAction<string[]>) => {
      state.customerFilterIds = [...action.payload];
    },
    setBookingsCarrierFilters: (state, action: PayloadAction<string[]>) => {
      state.carrierFilterIds = [...action.payload];
    },
    setBookingsStatusFilters: (state, action: PayloadAction<string[]>) => {
      state.statusFilterValues = [...action.payload];
    },
    setBookingsBookingTypeFilters: (state, action: PayloadAction<string[]>) => {
      state.bookingTypeFilterValues = [...action.payload];
    },
    setBookingsLocationFilters: (state, action: PayloadAction<string[]>) => {
      state.locationFilterIds = [...action.payload];
    },
    setBookingsDateRange: (
      state,
      action: PayloadAction<{ dateFrom: string | null; dateTo: string | null }>,
    ) => {
      state.dateFrom = action.payload.dateFrom;
      state.dateTo = action.payload.dateTo;
    },
    setBookingsListRange: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      state.fromIndex = action.payload.fromIndex;
      state.toIndex = action.payload.toIndex;
    },
    setBookingsScopeCustomerId: (state, action: PayloadAction<string | null>) => {
      state.scopeCustomerId = action.payload;
      state.scopeCarrierId = null;
    },
    setBookingsScopeCarrierId: (state, action: PayloadAction<string | null>) => {
      state.scopeCarrierId = action.payload;
      state.scopeCustomerId = null;
    },
    markBookingDeleted: (state, action: PayloadAction<string>) => {
      const row = state.rows.find((r) => r.id === action.payload);
      if (row) {
        row.status = "DELETED";
      }
    },
    prepareBookingsListForCustomerScope: (
      state,
      action: PayloadAction<PrepareBookingsListForCustomerScopePayload>,
    ) => {
      state.search = "";
      state.statusFilterValues = [];
      state.bookingTypeFilterValues = [];
      state.customerFilterIds = [];
      state.carrierFilterIds = [];
      state.locationFilterIds = [];
      state.scopeCustomerId = action.payload.customerId;
      state.scopeCarrierId = null;
      state.dateFrom = action.payload.dateFrom;
      state.dateTo = action.payload.dateTo;
      state.sortingField = action.payload.sortingField;
      state.sortingOrder = action.payload.sortingOrder;
      state.fromIndex = action.payload.fromIndex;
      state.toIndex = action.payload.toIndex;
      state.rows = [];
      state.totalBookings = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookings.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.latestFetchRequestId = action.meta.requestId;
        state.rows = [];
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestFetchRequestId) {
          return;
        }
        state.loading = false;
        state.totalBookings = action.payload.data.totalBookings;
        state.rows = mapApiBookingsToBookingRows(action.payload.data.bookings);
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestFetchRequestId) {
          return;
        }
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch bookings";
      });
  },
});

export const {
  setBookingsSearch,
  setBookingsSort,
  setBookingsCustomerFilters,
  setBookingsCarrierFilters,
  setBookingsStatusFilters,
  setBookingsBookingTypeFilters,
  setBookingsLocationFilters,
  setBookingsDateRange,
  setBookingsListRange,
  setBookingsScopeCustomerId,
  setBookingsScopeCarrierId,
  markBookingDeleted,
  prepareBookingsListForCustomerScope,
} = bookingsListSlice.actions;
export default bookingsListSlice.reducer;
